import { useTheme } from 'styled-components';
import {
  Content,
  Icon,
  Inline,
  InlineItem,
  Stack,
  Text,
} from '@kinesis/bungle';
import { CommunitySubSector, PathwayEvaluationConfigured } from '@/types';
import { Layout } from '@/components/layout';
import {
  AnnualEmissions,
  BudgetIndicators,
  EmissionsReductions,
} from '@/components/scenario-outputs';
import {
  toAnnualEmissions,
  toBudgetOutput,
  toReductions,
} from '@/data/pathways';
import { renderCommunitySubSector } from '@/data/labels';

const listOf = (labels: string[]) =>
  labels.length <= 1
    ? (labels[0] ?? '')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

// The undriven sub-sectors, surfaced: their streams hold flat and growth
// intents won't move them until their driver metric has data — a normal
// state (degrade and surface), never an error.
const UndrivenNotice = (props: { undriven: CommunitySubSector[] }) => {
  const theme = useTheme() as any;
  return (
    <Content
      background='white'
      padding='medium'
      paddingMode='equal'
      border
      borderRadiusSize='small'
    >
      <Inline space='small' alignY='center'>
        <InlineItem sizing='fit-content'>
          <Icon type='info-circle' color={theme.color.yellow6} />
        </InlineItem>
        <InlineItem sizing='fill-container'>
          <Text>
            Growth won’t affect{' '}
            {listOf(props.undriven.map(renderCommunitySubSector))} until you
            provide dwellings or employment data for{' '}
            {props.undriven.length === 1 ? 'it' : 'them'}.
          </Text>
        </InlineItem>
      </Inline>
    </Content>
  );
};

// An evaluated pathway's results: the carbon budget (one scalar, a
// remaining figure per ambition evaluation), the annual trajectory, and
// the per-intervention reductions — rendered through the legacy
// scenario-outputs charts (same display, new wire; reshaped, never
// rescaled — the unit sits once at the evaluation's scope). The download
// lives on the toolbar, not in the body.
const PathwayResults = (props: PathwayResultsProps) => {
  const { evaluation, showUndrivenNotice = false } = props;
  const budget = toBudgetOutput(evaluation);
  return (
    <Layout>
      <Stack space='large'>
        {showUndrivenNotice && evaluation.undriven.length > 0 && (
          <UndrivenNotice undriven={evaluation.undriven} />
        )}
        {budget && (
          <BudgetIndicators budget={budget} total={evaluation.budget?.budget} />
        )}
        <AnnualEmissions emissions={toAnnualEmissions(evaluation)} />
        <EmissionsReductions reductions={toReductions(evaluation)} />
      </Stack>
    </Layout>
  );
};

export type PathwayResultsProps = {
  evaluation: PathwayEvaluationConfigured;
  showUndrivenNotice?: boolean;
};

export { PathwayResults };
